import classNames from "classnames"
import React from "react"
import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { useTranslation } from "react-i18next"
import { ReactComponent as TickIcon } from "@icons/TickIcon.svg"
import { IPdfPage, IPdfRow, showPrice } from "@services/pdf/types"
import * as styles from "./PdfBuilder.module.scss"
import { useBuilder } from "./context"
import { usePageThumbnail } from "./hooks/usePageThumbnail"

/** One selectable page thumbnail with an app-style checkbox indicator. */
export const PageThumb: React.FC<{
	page: IPdfPage
	customInput: string
	onToggle: () => void
	attachmentShadow?: boolean
}> = ({ page, customInput, onToggle, attachmentShadow }) => {
	const { state, strings } = useBuilder()
	const { dataUrl, loading } = usePageThumbnail(page.type, page.imageUrl, customInput, state.options, strings)

	return (
		<button
			type="button"
			className={classNames(styles.thumb, {
				[styles.thumbSelected]: page.isSelected,
				[styles.attachmentThumb]: !!attachmentShadow,
			})}
			onClick={onToggle}
			aria-pressed={page.isSelected}
			data-testid="pdf-page-thumb"
		>
			{dataUrl ? <img src={dataUrl} alt="" /> : <span className={styles.thumbLoading}>{loading ? "…" : ""}</span>}
			<span className={classNames(styles.thumbCheck, { [styles.thumbCheckChecked]: page.isSelected })}>
				<TickIcon />
			</span>
		</button>
	)
}

/** Artwork row: page variant thumbnails + optional price override. */
export const ArtworkRow: React.FC<{ row: IPdfRow; index: number }> = ({ row, index }) => {
	const { state, dispatch } = useBuilder()
	const { t } = useTranslation()
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.id })

	return (
		<div
			ref={setNodeRef}
			style={{ transform: CSS.Transform.toString(transform), transition: transition ?? undefined }}
			className={classNames(styles.row, { [styles.rowDragging]: isDragging })}
			data-testid={`pdf-row-${index}`}
		>
			<button
				type="button"
				className={styles.dragHandle}
				{...attributes}
				{...listeners}
				aria-label="Reorder"
				title="Drag to reorder"
			>
				⠿
			</button>
			<div className={styles.rowMain}>
				<div className={styles.rowThumbs}>
					{row.pages.map((page) => (
						<PageThumb
							key={page.id}
							page={page}
							customInput={row.customInput}
							onToggle={() => dispatch({ type: "togglePage", rowId: row.id, pageId: page.id })}
						/>
					))}
				</div>
				{showPrice(state.options) && (
					<input
						type="text"
						className={classNames(styles.textInput, styles.priceInput)}
						value={row.customInput}
						placeholder={t("pdfBuilder.priceHint")}
						aria-label={t("pdfBuilder.priceLabel")}
						onChange={(e) => dispatch({ type: "setRowInput", rowId: row.id, value: e.target.value })}
					/>
				)}
			</div>
		</div>
	)
}

/** Attachment row: "{artist} info" caption + shadowed thumbnail. */
export const AttachmentRow: React.FC<{ row: IPdfRow; index: number }> = ({ row, index }) => {
	const { dispatch } = useBuilder()
	const { t } = useTranslation()
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.id })

	return (
		<div
			ref={setNodeRef}
			style={{ transform: CSS.Transform.toString(transform), transition: transition ?? undefined }}
			className={classNames(styles.row, { [styles.rowDragging]: isDragging })}
			data-testid={`pdf-row-${index}`}
		>
			<button
				type="button"
				className={styles.dragHandle}
				{...attributes}
				{...listeners}
				aria-label="Reorder"
				title="Drag to reorder"
			>
				⠿
			</button>
			<div className={styles.rowMain}>
				<p className={styles.rowCaption}>
					<span className={styles.rowCaptionArtist}>
						{t("pdfBuilder.attachmentLabel", { artist: row.artist ?? "" })}
					</span>
				</p>
				<div className={styles.rowThumbs}>
					{row.pages.map((page) => (
						<PageThumb
							key={page.id}
							page={page}
							customInput=""
							attachmentShadow
							onToggle={() => dispatch({ type: "togglePage", rowId: row.id, pageId: page.id })}
						/>
					))}
				</div>
			</div>
		</div>
	)
}
